import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, Pencil, Trash2, PiggyBank, Target, TriangleAlert } from "lucide-react";
import {
    usePlan,
    useReferences,
    useUpdatePlanSettings,
    useCreatePlanEnvelope,
    useUpdatePlanEnvelope,
    useDeletePlanEnvelope,
    useBudgets,
    useGoals,
} from "@/api/queries";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { ErrorState } from "@/components/ErrorState";
import { InfoTip } from "@/components/InfoTip";
import { useToast } from "@/components/Toast";
import { formatAmount, cn } from "@/lib/utils";
import type { PlanEnvelope, PlanEtaScenario } from "@/api/types";

const eur = (x: number) => `${formatAmount(x, "EUR")} €`;

const MONTHS_NOM = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
function monthLabel(ym: string): string {
    const [y, m] = ym.split("-").map(Number);
    return `${MONTHS_NOM[m - 1] ?? ""} ${y}`;
}

/** Месяцы → «N лет M мес»: 134 месяца читаются хуже, чем «11 лет 2 мес». */
function humanMonths(months: number): string {
    const y = Math.floor(months / 12);
    const m = months % 12;
    if (y === 0) return `${m} мес`;
    if (m === 0) return `${y} ${y === 1 ? "год" : y < 5 ? "года" : "лет"}`;
    return `${y} ${y === 1 ? "год" : y < 5 ? "года" : "лет"} ${m} мес`;
}

function ScenarioCell({ label, s, hint }: { label: string; s: PlanEtaScenario; hint: string }) {
    return (
        <div className="rounded-xl border border-border/60 p-3 space-y-1">
            <InfoTip text={hint}>
                <div className="text-xs text-muted-foreground">{label}</div>
            </InfoTip>
            <div className={cn("text-lg font-semibold tabular-nums", !s.reachable && "text-muted-foreground")}>
                {s.reachable && s.months != null ? humanMonths(s.months) : "не достигается"}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">{eur(s.rate_eur)} / мес</div>
        </div>
    );
}

type EnvelopeModal =
    | { mode: "create" }
    | { mode: "edit"; envelope: PlanEnvelope }
    | null;

export function PlanPage() {
    const { data, isLoading, isError, refetch } = usePlan();
    const refs = useReferences();
    const budgets = useBudgets();
    const toast = useToast();

    const updateSettings = useUpdatePlanSettings();
    const createEnvelope = useCreatePlanEnvelope();
    const updateEnvelope = useUpdatePlanEnvelope();
    const deleteEnvelope = useDeletePlanEnvelope();

    const [goalInput, setGoalInput] = useState("");
    const [floorInput, setFloorInput] = useState("0");
    const [bufferInput, setBufferInput] = useState("0");
    const [kInput, setKInput] = useState("0.78");
    const [goalId, setGoalId] = useState("");
    const [modal, setModal] = useState<EnvelopeModal>(null);
    const goals = useGoals("active");

    // Форма заполняется ОДИН раз, при первой загрузке. Зависимость от всего `data`
    // означала бы: фоновый refetch (staleTime 60 с) затирает несохранённый ввод
    // прямо под руками. `goal_id` берётся только из настроек — подставлять сюда
    // цель из ETA нельзя, иначе сохранение молча превращает режим «первая активная»
    // в жёсткую привязку к конкретной цели.
    const formInitialized = useRef(false);
    useEffect(() => {
        if (!data || formInitialized.current) return;
        formInitialized.current = true;
        const s = data.settings;
        setGoalInput(s.monthly_savings_goal_eur != null ? String(s.monthly_savings_goal_eur) : "");
        setFloorInput(String(s.savings_floor_eur));
        setBufferInput(String(s.buffer_eur));
        setKInput(String(s.optimistic_k));
        setGoalId(s.goal_id ?? "");
    }, [data]);

    // Категории, которые ещё можно отдать конверту: расходные, активные,
    // не под конвертом и не под месячным лимитом (инвариант XOR, SPEC-057).
    const availableCategories = useMemo(() => {
        const taken = new Set((data?.envelopes ?? []).map(e => e.category_id));
        const limited = new Set((budgets.data?.categories ?? []).map(c => c.category_id));
        return (refs.data?.categories ?? []).filter(
            c => c.type === "expense" && c.is_active && !taken.has(c.id) && !limited.has(c.id),
        );
    }, [refs.data, data?.envelopes, budgets.data]);

    if (isError) return <ErrorState onRetry={() => refetch()} label="Не удалось загрузить план" />;
    if (isLoading || !data) return <div className="card p-8 text-center text-muted-foreground">Загрузка…</div>;

    const { ceiling, envelopes, cascade, eta, settings, data_quality } = data;

    const num = (raw: string) => Number(raw.trim().replace(",", "."));

    async function saveSettings() {
        const rawGoal = goalInput.trim();
        const goalValue = rawGoal === "" ? null : num(rawGoal);
        if (goalValue != null && (!Number.isFinite(goalValue) || goalValue < 0)) {
            toast("Введите неотрицательное число", "err");
            return;
        }
        const floor = num(floorInput || "0");
        const buffer = num(bufferInput || "0");
        const k = num(kInput || "0.78");
        if (!Number.isFinite(floor) || floor < 0 || !Number.isFinite(buffer) || buffer < 0) {
            toast("Пол взноса и буфер — неотрицательные числа", "err");
            return;
        }
        // Границы те же, что в CHECK миграции: измеренный эффект получен на срезе 20–25 %.
        if (!Number.isFinite(k) || k < 0.7 || k > 0.85) {
            toast("Коэффициент оптимистичного режима — от 0,70 до 0,85", "err");
            return;
        }
        const saved = await updateSettings.mutateAsync({
            monthly_savings_goal_eur: goalValue,
            savings_floor_eur: floor,
            buffer_eur: buffer,
            optimistic_k: k,
            goal_id: goalId || null,
        });
        // Поля заполняются ИЗ ОТВЕТА мутации: сервер мог нормализовать значения, и
        // показывать вместо принятого свой ввод — вранье. Сбрасывать флаг первичной
        // инициализации при этом нельзя: тогда следующий фоновый refetch затёр бы
        // ввод, который человек уже начал печатать после сохранения.
        const s = saved.settings;
        setGoalInput(s.monthly_savings_goal_eur != null ? String(s.monthly_savings_goal_eur) : "");
        setFloorInput(String(s.savings_floor_eur));
        setBufferInput(String(s.buffer_eur));
        setKInput(String(s.optimistic_k));
        setGoalId(s.goal_id ?? "");
        toast(goalValue == null ? "Цель накоплений снята" : `Цель накоплений: ${eur(goalValue)} в месяц`);
    }

    return (
        <div className="space-y-6">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <h1 className="text-2xl font-semibold">План · {monthLabel(data.month)}</h1>
                {((data_quality.imbalance_recent_eur != null && Math.abs(data_quality.imbalance_recent_eur) >= 1) || data_quality.missing_rates > 0) && (
                    <InfoTip
                        text="План строится по записанным тратам. Журнал невязок за последние 90 дней показывает, насколько самоотчёт расходится с фактом балансов; числа плана на эту величину не корректируются."
                        side="bottom"
                        align="end"
                    >
                        <span className="text-xs text-muted-foreground">
                            по записанному
                            {data_quality.imbalance_recent_eur != null && Math.abs(data_quality.imbalance_recent_eur) >= 1
                                && ` · невязка 90 дн: ${eur(data_quality.imbalance_recent_eur)}`}
                            {data_quality.missing_rates > 0 && ` · записей без курса: ${data_quality.missing_rates}`}
                        </span>
                    </InfoTip>
                )}
            </div>

            {/* ── Потолок месяца ─────────────────────────────────────────── */}
            <section className="card p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <h2 className="font-medium">Сколько можно потратить</h2>
                </div>

                {ceiling ? (
                    <div className="space-y-3">
                        <div className="flex items-end gap-3 flex-wrap">
                            <div className="text-4xl font-semibold tabular-nums">{eur(ceiling.remaining_eur)}</div>
                            <div className="text-sm text-muted-foreground pb-1">
                                осталось из {eur(ceiling.limit_eur)} · потрачено {eur(ceiling.spent_eur)}
                            </div>
                        </div>
                        <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                            <div
                                className="h-full rounded-full transition-[width] duration-500"
                                style={{
                                    width: `${Math.min(100, Math.max(2, ceiling.limit_eur > 0 ? (ceiling.spent_eur / ceiling.limit_eur) * 100 : 100))}%`,
                                    background: ceiling.remaining_eur < 0 ? "#ef4444" : ceiling.remaining_eur < 0.2 * ceiling.limit_eur ? "#f59e0b" : "#10b981",
                                }}
                            />
                        </div>
                        <InfoTip
                            text={
                                ceiling.binding === "goal"
                                    ? `Потолок задан целью накоплений: медиана дохода за ${ceiling.months_used} мес минус взнос в цель минус отчисления в конверты. Оптимистичный срез дал бы ${eur(ceiling.optimistic_cap_eur)}.`
                                    : `Потолок задан оптимистичным режимом: ${Math.round((1 - settings.optimistic_k) * 100)} % ниже привычных трат за ${ceiling.months_used} мес. Цель накоплений позволила бы ${eur(ceiling.goal_cap_eur)}.`
                            }
                        >
                            <span className="text-sm text-muted-foreground">
                                {ceiling.binding === "goal" ? "ограничивает цель накоплений" : `ограничивает оптимистичный режим (−${Math.round((1 - settings.optimistic_k) * 100)} %)`}
                            </span>
                        </InfoTip>
                        <p className="text-xs text-muted-foreground">
                            Считаются траты вне конвертов: крупные покупки живут в своих годовых циклах.
                        </p>
                        {/* SPEC-058: лимиты категорий — разбивка этого числа */}
                        {budgets.data?.allocation && (
                            <div className="rounded-lg border p-3 space-y-2" data-testid="ceiling-breakdown">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-medium">Разбивка потолка</div>
                                    <Link to="/budgets" className="text-xs text-muted-foreground underline">лимиты категорий</Link>
                                </div>
                                {(() => {
                                    const a = budgets.data!.allocation!;
                                    const rows: Array<[string, number, string?]> = [
                                        ["Лимиты категорий", a.allocated_eur],
                                        ["Прочее (без лимита)", a.pool_eur, "траты без категории и категории без лимита"],
                                    ];
                                    return (
                                        <div className="text-sm space-y-1">
                                            {rows.map(([label, v, tip]) => (
                                                <div key={label} className="flex justify-between gap-3" title={tip}>
                                                    <span className="text-muted-foreground">{label}</span>
                                                    <span className="tabular-nums">{eur(v)}</span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between gap-3 border-t pt-1">
                                                {!a.feasible ? (
                                                    <span className="text-destructive">План не сходится: минимумы и ручные лимиты больше потолка — см. каскад ниже</span>
                                                ) : Math.abs(a.unallocated_eur) < 0.5 ? (
                                                    <span className="text-muted-foreground">Распределено полностью</span>
                                                ) : a.unallocated_eur > 0 ? (
                                                    <><span className="text-muted-foreground">Нераспределено</span><span className="tabular-nums text-positive">{eur(a.unallocated_eur)}</span></>
                                                ) : (
                                                    <><span className="text-muted-foreground">Сверх плана</span><span className="tabular-nums text-destructive">{eur(-a.unallocated_eur)}</span></>
                                                )}
                                            </div>
                                            {a.alpha < 1 && a.feasible && (
                                                <p className="text-xs text-muted-foreground pt-1">
                                                    Категории ужаты на {Math.round((1 - a.alpha) * 100)} % от привычного уровня. Меньше «прочего» — точнее разбивка: дайте крупным категориям лимит или конверт.
                                                </p>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        Задайте месячную цель накоплений — и здесь появится одно число «сколько можно потратить».
                    </p>
                )}

                <div className="flex items-end gap-3 flex-wrap pt-1">
                    <label className="space-y-1">
                        <span className="text-xs text-muted-foreground block">Откладывать в месяц, €</span>
                        <input
                            className="w-40 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                            inputMode="decimal"
                            value={goalInput}
                            onChange={e => setGoalInput(e.target.value)}
                            placeholder="например 1200"
                        />
                    </label>
                    <label className="space-y-1">
                        <InfoTip text="Ниже этой суммы каскад не опускает взнос в цель в тяжёлый месяц.">
                            <span className="text-xs text-muted-foreground block">Пол взноса, €</span>
                        </InfoTip>
                        <input
                            className="w-28 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                            inputMode="decimal" value={floorInput} onChange={e => setFloorInput(e.target.value)}
                        />
                    </label>
                    <label className="space-y-1">
                        <InfoTip text="Свободные деньги, которыми каскад закрывает дефицит месяца в первую очередь.">
                            <span className="text-xs text-muted-foreground block">Буфер, €</span>
                        </InfoTip>
                        <input
                            className="w-28 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                            inputMode="decimal" value={bufferInput} onChange={e => setBufferInput(e.target.value)}
                        />
                    </label>
                    <label className="space-y-1">
                        <InfoTip text="Доля от привычных трат в оптимистичном режиме: 0,78 — это «на 22 % ниже факта». Допустимо 0,70…0,85.">
                            <span className="text-xs text-muted-foreground block">Коэффициент</span>
                        </InfoTip>
                        <input
                            className="w-24 px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums"
                            inputMode="decimal" value={kInput} onChange={e => setKInput(e.target.value)}
                        />
                    </label>
                    {(goals.data?.goals?.length ?? 0) > 1 && (
                        <label className="space-y-1">
                            <span className="text-xs text-muted-foreground block">Цель для срока</span>
                            <Select value={goalId} onChange={e => setGoalId(e.target.value)}>
                                <option value="">первая активная</option>
                                {(goals.data?.goals ?? []).filter(g => g.target_amount != null).map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </Select>
                        </label>
                    )}
                    <button className="btn-primary" onClick={saveSettings} disabled={updateSettings.isPending}>
                        Сохранить
                    </button>
                </div>
            </section>

            {/* ── Каскад ─────────────────────────────────────────────────── */}
            {cascade.active && (
                <section className="card p-5 space-y-3 border-amber-500/40">
                    <div className="flex items-center gap-2">
                        <TriangleAlert className="h-4 w-4 text-amber-500" />
                        <h2 className="font-medium">План не сходится: не хватает {eur(cascade.deficit_eur)}</h2>
                    </div>
                    <ol className="space-y-1.5 text-sm">
                        {cascade.steps.map((s, i) => (
                            <li key={i} className="flex justify-between gap-4 rounded-lg px-2 py-1 hover:bg-secondary/40 transition-colors">
                                <span>{i + 1}. {s.label}</span>
                                {s.amount_eur != null && <span className="tabular-nums text-muted-foreground">{eur(s.amount_eur)}</span>}
                            </li>
                        ))}
                    </ol>
                    <p className="text-xs text-muted-foreground">
                        Это рекомендация: ни цель, ни конверты автоматически не меняются.
                    </p>
                </section>
            )}

            {/* ── Конверты ───────────────────────────────────────────────── */}
            <section className="card p-5 space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <PiggyBank className="h-4 w-4 text-muted-foreground" />
                        <InfoTip text="Годовой конверт для редких крупных покупок: остаток переносится между месяцами, а перерасход виден минусом. У категории с конвертом месячного лимита нет — иначе одна трата считалась бы дважды.">
                            <h2 className="font-medium">Годовые конверты</h2>
                        </InfoTip>
                    </div>
                    <button className="btn-ghost" onClick={() => setModal({ mode: "create" })}>
                        <Plus className="h-4 w-4" /> Добавить
                    </button>
                </div>

                {envelopes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        Конвертов нет. Кандидаты — категории с редкими крупными тратами: техника, одежда, дом.
                    </p>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                        {envelopes.map(e => {
                            const pct = e.annual_target_eur > 0 ? (e.spent_cycle_eur / e.annual_target_eur) * 100 : 0;
                            return (
                                <div key={e.id} className="rounded-xl border border-border/60 p-4 space-y-2 hover:border-border transition-colors">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-medium truncate">
                                            {e.emoji ? `${e.emoji} ` : ""}{e.name}
                                            {e.source === "auto" && (
                                                <span
                                                    className="ml-2 align-middle text-[11px] font-normal px-1.5 py-0.5 rounded-full bg-secondary/70 text-muted-foreground"
                                                    title="Годовой бюджет по истории (редкие крупные покупки). Поправьте сумму — конверт закрепится и пересчитываться не будет."
                                                    data-testid="envelope-auto-badge"
                                                >
                                                    авто
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                className="btn-icon"
                                                aria-label="Изменить конверт"
                                                onClick={() => setModal({ mode: "edit", envelope: e })}
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button
                                                className="btn-icon"
                                                aria-label="Удалить конверт"
                                                onClick={async () => {
                                                    await deleteEnvelope.mutateAsync(e.id);
                                                    toast(`Конверт «${e.name}» снят`);
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className={cn("text-2xl font-semibold tabular-nums", e.overspent && "text-destructive")}>
                                            {eur(e.balance_eur)}
                                        </span>
                                        <span className="text-xs text-muted-foreground">накоплено</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-[width] duration-500"
                                            style={{
                                                width: `${Math.min(100, Math.max(2, pct))}%`,
                                                background: e.overspent ? "#ef4444" : pct >= 80 ? "#f59e0b" : "#10b981",
                                            }}
                                        />
                                    </div>
                                    <div className="text-xs text-muted-foreground tabular-nums">
                                        {eur(e.spent_cycle_eur)} из {eur(e.annual_target_eur)} за цикл · взнос {eur(e.accrual_eur)}/мес
                                    </div>
                                    {e.overspent && (
                                        <div className="text-xs text-destructive">
                                            Перерасход: учётный счётчик, не долг по счетам.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ── Цель ───────────────────────────────────────────────────── */}
            {eta && (
                <section className="card p-5 space-y-4">
                    <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        <h2 className="font-medium">
                            {eta.goal_name}: осталось {eur(eta.remaining_eur)} из {eur(eta.target_eur)}
                        </h2>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-4">
                        <ScenarioCell label="Плохой сценарий" s={eta.p25} hint={`Нижний квартиль месячных сальдо за ${eta.months_used} наблюдённых месяцев.`} />
                        <ScenarioCell label="Обычный" s={eta.p50} hint="Медиана месячных сальдо. При редких крупных тратах медиана оптимистичнее среднего — поэтому рядом стоит плохой сценарий." />
                        <ScenarioCell label="Хороший" s={eta.p75} hint="Верхний квартиль месячных сальдо." />
                        {eta.at_goal && (
                            <ScenarioCell label="При взносе по плану" s={eta.at_goal} hint="Срок, если каждый месяц откладывать ровно заданную сумму." />
                        )}
                    </div>
                </section>
            )}

            {modal && (
                <EnvelopeModal
                    state={modal}
                    categories={availableCategories}
                    onClose={() => setModal(null)}
                    onCreate={async (categoryId, annual, priority) => {
                        await createEnvelope.mutateAsync({ category_id: categoryId, annual_target_eur: annual, priority });
                        toast("Конверт создан");
                        setModal(null);
                    }}
                    onUpdate={async (id, annual, priority) => {
                        await updateEnvelope.mutateAsync({ id, patch: { annual_target_eur: annual, priority } });
                        toast("Конверт обновлён");
                        setModal(null);
                    }}
                />
            )}
        </div>
    );
}

function EnvelopeModal({
    state,
    categories,
    onClose,
    onCreate,
    onUpdate,
}: {
    state: NonNullable<EnvelopeModal>;
    categories: Array<{ id: string; name: string; emoji?: string | null }>;
    onClose: () => void;
    onCreate: (categoryId: string, annual: number, priority: number) => Promise<void>;
    onUpdate: (id: string, annual: number, priority: number) => Promise<void>;
}) {
    const isEdit = state.mode === "edit";
    const [categoryId, setCategoryId] = useState(isEdit ? state.envelope.category_id : categories[0]?.id ?? "");
    const [annual, setAnnual] = useState(isEdit ? String(state.envelope.annual_target_eur) : "");
    const [priority, setPriority] = useState(isEdit ? String(state.envelope.priority) : "0");
    const toast = useToast();

    const submit = async () => {
        const value = Number(annual.trim().replace(",", "."));
        if (!Number.isFinite(value) || value <= 0) {
            toast("Годовой бюджет должен быть положительным", "err");
            return;
        }
        const prio = Number(priority) || 0;
        if (isEdit) await onUpdate(state.envelope.id, value, prio);
        else await onCreate(categoryId, value, prio);
    };

    return (
        <Modal
            open
            onClose={onClose}
            title={isEdit ? `Конверт «${state.envelope.name}»` : "Новый годовой конверт"}
        >
            <div className="space-y-4">
                {!isEdit && (
                    <label className="space-y-1 block">
                        <span className="text-xs text-muted-foreground">Категория</span>
                        {categories.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                Нет доступных категорий: у остальных уже есть месячный лимит или конверт.
                            </p>
                        ) : (
                            <Select fullWidth value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{`${c.emoji ? c.emoji + " " : ""}${c.name}`}</option>
                                ))}
                            </Select>
                        )}
                    </label>
                )}
                <label className="space-y-1 block">
                    <span className="text-xs text-muted-foreground">Годовой бюджет, €</span>
                    <input className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums" inputMode="decimal" value={annual} onChange={e => setAnnual(e.target.value)} />
                </label>
                <label className="space-y-1 block">
                    <span className="text-xs text-muted-foreground">Приоритет урезания (больше — режется раньше)</span>
                    <input className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring tabular-nums" inputMode="numeric" value={priority} onChange={e => setPriority(e.target.value)} />
                </label>
                <p className="text-xs text-muted-foreground">
                    Пока категория под конвертом, месячный лимит на неё поставить нельзя — это защита от двойного учёта.
                </p>
                <div className="flex justify-end gap-2 pt-2">
                    <button className="btn-ghost" onClick={onClose}>Отмена</button>
                    <button className="btn-primary" onClick={submit} disabled={!isEdit && !categoryId}>Сохранить</button>
                </div>
            </div>
        </Modal>
    );
}
